import { useState, useEffect } from 'react'
import Select from "react-select";
import DatePicker from "react-datepicker";
import ToastrService from "../../services/toastService";
import { useAddAgentMutation, useGetAgentsByQueueMutation, useGetSupervisorsByCampaignMutation } from './Api';


const formatDateForApi = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const AddAgentModal = ({ show, onClose, onSave, campaignId, queueId }) => {

    const [selectedAgent, setSelectedAgent] = useState([]);
    const [selectedSupervisors, setSelectedSupervisors] = useState([]);
    const [supervisors, setSupervisors] = useState([]);
    const [effectiveFrom, setEffectiveFrom] = useState(null);
    const [agents, setAgents] = useState([]);

    const [getSupervisorsByCampaign] = useGetSupervisorsByCampaignMutation();
    const [getAgentsByQueue] = useGetAgentsByQueueMutation();
    const [addAgentsToQueue, { isLoading }] = useAddAgentMutation();


    const agentOptions = agents.map((agent) => ({
        value: agent.id,
        label: agent.name,
        flexzId: agent.flexz_id,
        supervisorId: agent.supervisor_id,
        supervisorName: agent.supervisor_name,
    }));

    const supervisorOptions = supervisors.map((supervisor) => ({
        value: supervisor.id,
        label: supervisor.name,
    }));

    useEffect(() => {
        if (!show) {
            return;
        }

        fetchSupervisors();
    }, [show, campaignId]);


    useEffect(() => {
        if (!show || !selectedSupervisors?.length) {
            setAgents([]);
            setSelectedAgent([]);
            return;
        }
        fetchAgents();
    }, [selectedSupervisors, show, campaignId, queueId]);

    const fetchSupervisors = async () => {
        if (!campaignId) {
            return;
        }
        try {
            const payload = {
                campaignId: campaignId,
            };

            const res = await getSupervisorsByCampaign(payload).unwrap();
            setSupervisors(res?.data || []);
        } catch (error) {
            console.error("Error fetching supervisors:", error);
            setSupervisors([]);
        }
    };


    const fetchAgents = async () => {
        if (!campaignId || !queueId) {
            setAgents([]);
            return;
        }
        try {
            const payload = {
                campaignId: campaignId,
                queueId: queueId,
                supervisors: selectedSupervisors.map((supervisor) => supervisor.value),
            };
            const res = await getAgentsByQueue(payload).unwrap();
            setAgents(res?.data || []);
        } catch (error) {
            console.error("Error fetching agents:", error);
            setAgents([]);
        }
    };


    const handleSave = async () => {
        if (!selectedAgent?.length || !selectedSupervisors?.length || !effectiveFrom) {
            return;
        }
        // const supervisorId = selectedAgent[0]?.supervisorId;

        const payload = {
            campaignId: campaignId,
            supervisorIds: selectedSupervisors.map((supervisor) => supervisor.value),
            agentIds: selectedAgent.map((agent) => agent.value),
            queueId: queueId,
            effectiveFrom: formatDateForApi(effectiveFrom),
        };

        try {
            const res = await addAgentsToQueue(payload).unwrap();
            if (res?.success) {
                ToastrService.success(res?.message || "Agents added successfully!");
                onSave();
            } else {
                ToastrService.error(res?.message || "Failed to add agents");
            }
        } catch (error) {
            console.error("Error adding agents:", error);
        }
    };

    const handleClose = () => {
        setSelectedAgent(null);
        setEffectiveFrom(null);
        setAgents([]);
        setSelectedSupervisors([]);
        onClose();
    };

    return (
        <>
            <div
                className={`modal fade ${show ? "show d-block" : ""}`}
                tabIndex="-1"
                role="dialog"
                aria-hidden={!show}
                style={{
                    backgroundColor: show ? "rgba(0, 0, 0, 0.5)" : "transparent",
                }}
            >
                <div
                    className="modal-dialog modal-md modal-dialog-centered"
                    role="document"
                >
                    <div className="modal-content">

                        {/* Header */}
                        <div className="modal-header py-3">
                            <h5 className="modal-title fw-semibold mb-0">
                                Add Agent
                            </h5>

                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleClose}
                            />
                        </div>

                        {/* Body */}
                        <div className="modal-body p-4">

                            <div className="mb-3">
                                <label className="form-label fw-medium">
                                    Supervisor
                                </label>

                                <Select
                                    options={supervisorOptions}
                                    value={selectedSupervisors}
                                    onChange={setSelectedSupervisors}
                                    placeholder="Select Supervisor"
                                    isClearable
                                    isMulti
                                    closeMenuOnSelect={false}
                                    menuPlacement="auto"
                                    classNamePrefix="react-select"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-medium">
                                    Agent
                                </label>

                                <Select
                                    options={agentOptions}
                                    value={selectedAgent}
                                    onChange={setSelectedAgent}
                                    placeholder="Select Agent"
                                    isClearable
                                    isMulti
                                    closeMenuOnSelect={false}
                                    menuPlacement="auto"
                                    classNamePrefix="react-select"
                                    formatOptionLabel={(option, { context }) => {
                                        if (context === "value") {
                                            return option.label;
                                        }

                                        return (
                                            <div className="d-flex flex-column">
                                                <span className="fw-semibold text-dark">
                                                    {option.label}
                                                </span>

                                                <span className="text-muted small">
                                                    {option.flexzId}
                                                    <span className="mx-1">·</span>
                                                    Supervisor: {option.supervisorName}
                                                </span>
                                            </div>
                                        );
                                    }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-medium">
                                    Effective from
                                </label>

                                <DatePicker
                                    selected={effectiveFrom}
                                    onChange={(date) => setEffectiveFrom(date)}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                    className="form-control"
                                    isClearable
                                    showPopperArrow={false}
                                />
                            </div>

                        </div>

                        {/* Footer */}
                        <div className="modal-footer py-2 px-4">
                            <button
                                type="button"
                                className="btn btn-light border px-3 py-2"
                                onClick={handleClose}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="btn btn-primary px-3 py-2"
                                onClick={handleSave}
                                disabled={!selectedSupervisors?.length || !selectedAgent?.length || !effectiveFrom || isLoading}
                            >
                                {isLoading ? "Saving..." : "Save"}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </>
    )
}

export default AddAgentModal